/*
 * DTFNY voice search.
 *
 * Wires the [data-voice-search] mic buttons inside the search drawer to the
 * Web Speech API. Click the mic -> request permission -> listen -> populate
 * the search input with the transcript, which automatically fires the
 * existing predictive-search input event.
 *
 * Feature detection: if SpeechRecognition isn't available (Firefox without
 * a flag, older browsers, hardware without a mic), the button stays hidden.
 * The HTML default for the button is `hidden` — this script unhides it
 * only when support is confirmed.
 */
(function () {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  // No support? Leave the mic button hidden. Nothing else to do.
  if (!SpeechRecognition) return;

  // Unhide all voice buttons currently in the DOM, and observe for any added later
  // (the search snippet renders inside <details-modal> which is in the page on load).
  const unhide = (root = document) => {
    root.querySelectorAll('[data-voice-search][hidden]').forEach((btn) => {
      btn.removeAttribute('hidden');
    });
  };
  unhide();

  // If the snippet is ever re-rendered (e.g. by sections-rendering API), catch it.
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1) unhide(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });

  // Delegate clicks so we don't need to bind per-button or worry about timing.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-voice-search]');
    if (!button) return;
    event.preventDefault();

    // If a previous recognition is still active on this button, stop it.
    if (button._recognition) {
      button._recognition.stop();
      return;
    }

    const form = button.closest('form');
    const input = form && form.querySelector('input[type="search"]');
    if (!input) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = document.documentElement.lang || 'en-US';
    recognition.maxAlternatives = 1;

    button._recognition = recognition;

    recognition.onstart = () => {
      button.classList.add('is-listening');
      button.setAttribute('aria-pressed', 'true');
    };

    const cleanup = () => {
      button.classList.remove('is-listening');
      button.removeAttribute('aria-pressed');
      button._recognition = null;
    };

    recognition.onend = cleanup;
    recognition.onerror = (e) => {
      // 'aborted' just means the user stopped early — not an error to surface.
      if (e.error && e.error !== 'aborted' && e.error !== 'no-speech') {
        console.warn('Voice search:', e.error);
      }
      cleanup();
    };

    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0].transcript)
        .join('')
        .trim();
      if (!transcript) return;

      // Set the input value and dispatch an input event so the existing
      // predictive-search custom element picks it up and fires a results fetch.
      input.value = transcript;
      input.dispatchEvent(new Event('input', { bubbles: true }));

      // Final (non-interim) result: focus the input so the user can edit if needed.
      if (event.results[event.results.length - 1].isFinal) {
        input.focus();
      }
    };

    try {
      recognition.start();
    } catch (err) {
      console.warn('Voice search start failed:', err);
      cleanup();
    }
  });
})();
